import React, { useState, useMemo } from 'react';
import { toGrams, solveMeatWeightForCooked, formulaTotalPct } from '../utils/calculations';
import styles from './ScaleModal.module.css';

export default function ScaleModal({ calc, onClose }) {
  const { getSavedRecipes, loadRecipe, typicalLinkWeightG } = calc;

  const [step,        setStep]        = useState('select');  // select | mode | preview
  const [baseRecipe,  setBaseRecipe]  = useState(null);
  const [scaleMode,   setScaleMode]   = useState('meat');    // meat | count
  const [targetMeat,  setTargetMeat]  = useState('');
  const [targetCount, setTargetCount] = useState('');
  const [targetUnit,  setTargetUnit]  = useState('Metric (g)');

  const savedRecipes = getSavedRecipes();

  // ── Derived: base meat weight in grams ────────────────────
  const baseMeatG = useMemo(() => {
    if (!baseRecipe) return 0;
    return toGrams(baseRecipe.meatWeight, baseRecipe.unitSystem);
  }, [baseRecipe]);

  // ── Derived: new meat weight in grams after scaling ───────
  const newMeatG = useMemo(() => {
    if (scaleMode === 'meat') {
      return toGrams(parseFloat(targetMeat) || 0, targetUnit);
    }
    if (scaleMode === 'count') {
      const count = parseInt(targetCount, 10) || 0;
      const lwg   = parseFloat(typicalLinkWeightG) || 120;
      if (count <= 0 || lwg <= 0) return 0;
      // The links come off the stuffer, so a link count describes STUFFABLE
      // weight. This used to size the batch off raw mix and ignore the
      // equipment loss entirely, which left every count-scaled batch short —
      // the same mistake as scaling on raw weight. Solve it properly instead:
      //   basis = (stuffable + equipLoss) / (totalPct/100)
      const stuffableNeeded = count * lwg;
      const pct   = formulaTotalPct(baseRecipe.rows || []);
      const equip = baseRecipe.equipLoss ?? 150;
      if (pct <= 0) return 0;
      return Math.round((stuffableNeeded + equip) / (pct / 100) * 10) / 10;
    }
    return 0;
  }, [scaleMode, targetMeat, targetUnit, targetCount, typicalLinkWeightG, baseMeatG, baseRecipe]);

  // ── Derived: scale factor ─────────────────────────────────
  const scaleFactor = baseMeatG > 0 ? newMeatG / baseMeatG : 1;

  // ── Derived: preview of scaled ingredients ────────────────
  const previewRows = useMemo(() => {
    if (!baseRecipe || newMeatG <= 0) return [];
    return (baseRecipe.rows || [])
      .filter(r => r.name && r.pct)
      .map(r => {
        const pct    = parseFloat(r.pct) || 0;
        const baseG  = pct / 100 * baseMeatG;
        const newG   = pct / 100 * newMeatG;
        return { ...r, pct, baseG, newG };
      });
  }, [baseRecipe, newMeatG, baseMeatG]);

  // ── Apply scaled recipe ────────────────────────────────────
  const applyScale = () => {
    if (!baseRecipe || newMeatG <= 0) return;
    const scaled = {
      ...baseRecipe,
      recipeName: baseRecipe.recipeName + ' (scaled)',
      meatWeight: targetUnit === 'Metric (g)' ? newMeatG : newMeatG / 453.592,
      unitSystem: targetUnit,
    };
    loadRecipe(scaled);
    onClose();
  };

  // ── Validation ────────────────────────────────────────────
  const meatValid  = scaleMode === 'meat'  && newMeatG > 0;
  const countValid = scaleMode === 'count' && parseInt(targetCount,10) > 0;
  const canPreview = (meatValid || countValid) && baseRecipe && baseMeatG > 0;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.header}>
          <div>
            <div className={styles.title}>Scale Recipe</div>
            <div className={styles.subtitle}>
              Adjust batch size while keeping all ratios identical
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* ── STEP 1: Select base recipe ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>
            <span className={styles.stepBadge}>1</span>
            Select Base Recipe
          </div>

          {savedRecipes.length === 0 ? (
            <div className={styles.empty}>
              No saved recipes yet. Save a recipe first, then use Scale.
            </div>
          ) : (
            <div className={styles.recipeList}>
              {[...savedRecipes].reverse().map(r => (
                <button
                  key={r.id}
                  className={`${styles.recipeBtn} ${baseRecipe?.id === r.id ? styles.recipeBtnActive : ''}`}
                  onClick={() => {
                    setBaseRecipe(r);
                    setStep('mode');
                  }}
                >
                  <div className={styles.recipeName}>{r.recipeName}</div>
                  <div className={styles.recipeMeta}>
                    {r.cookMethod} · {r.meatWeight} {r.unitSystem === 'Metric (g)' ? 'g' : 'lbs'}
                    {' · '}{new Date(r.savedAt).toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── STEP 2: Choose scaling mode ── */}
        {baseRecipe && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>
              <span className={styles.stepBadge}>2</span>
              Scale By
            </div>

            <div className={styles.modeCards}>

              {/* Meat weight mode */}
              <button
                className={`${styles.modeCard} ${scaleMode === 'meat' ? styles.modeCardActive : ''}`}
                onClick={() => setScaleMode('meat')}
              >
                <div className={styles.modeIcon}>⚖️</div>
                <div className={styles.modeTitle}>Meat Weight</div>
                <div className={styles.modeDesc}>
                  Set a new total meat weight. All ingredients scale proportionally.
                </div>
              </button>

              {/* Sausage count mode */}
              <button
                className={`${styles.modeCard} ${scaleMode === 'count' ? styles.modeCardActive : ''}`}
                onClick={() => setScaleMode('count')}
              >
                <div className={styles.modeIcon}>🌭</div>
                <div className={styles.modeTitle}>Sausage Count</div>
                <div className={styles.modeDesc}>
                  Enter a target number of sausages. Uses Typical Link Weight
                  ({typicalLinkWeightG}g) and accounts for equipment loss.
                </div>
              </button>

            </div>

            {/* Input for chosen mode */}
            {scaleMode === 'meat' && (
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>
                  Target Meat Weight
                  <span className={styles.baseNote}>
                    (base: {baseMeatG.toFixed(0)}g)
                  </span>
                </label>
                <div className={styles.inputRow}>
                  <input
                    className={styles.numInput}
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 2000"
                    value={targetMeat}
                    onChange={e => setTargetMeat(e.target.value)}
                    autoFocus
                  />
                  <select
                    className={styles.unitSel}
                    value={targetUnit}
                    onChange={e => setTargetUnit(e.target.value)}
                  >
                    <option value="Metric (g)">g</option>
                    <option value="Imperial (lbs)">lbs</option>
                  </select>
                </div>
                {meatValid && (
                  <div className={styles.factorNote}>
                    Scale factor: ×{scaleFactor.toFixed(3)}
                    {' · '}
                    {scaleFactor > 1
                      ? `+${((scaleFactor - 1) * 100).toFixed(0)}% larger`
                      : `${((1 - scaleFactor) * 100).toFixed(0)}% smaller`}
                  </div>
                )}
              </div>
            )}

            {scaleMode === 'count' && (
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>
                  Target Sausage Count
                  <span className={styles.baseNote}>
                    using {typicalLinkWeightG}g per link — edit in Recipe Stats sidebar
                  </span>
                </label>
                <div className={styles.inputRow}>
                  <input
                    className={styles.numInput}
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 24"
                    value={targetCount}
                    onChange={e => setTargetCount(e.target.value)}
                    autoFocus
                  />
                  <span className={styles.unitLabel}>sausages</span>
                </div>
                {countValid && newMeatG > 0 && (
                  <div className={styles.factorNote}>
                    Requires ~{newMeatG.toFixed(0)}g meat+fat
                    {' · '}
                    scale factor ×{scaleFactor.toFixed(3)}
                    {' · '}
                    includes {baseRecipe.equipLoss ?? 150}g equipment loss
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── STEP 3: Preview ── */}
        {canPreview && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>
              <span className={styles.stepBadge}>3</span>
              Preview — Scaled Ingredient Amounts
            </div>

            <div className={styles.previewTable}>
              <div className={styles.previewHeader}>
                <span>Ingredient</span>
                <span>Base (g)</span>
                <span>Scaled (g)</span>
                <span>%</span>
              </div>
              {previewRows.map(r => (
                <div key={r.id} className={styles.previewRow}>
                  <span className={styles.previewName}>{r.name}</span>
                  <span className={styles.previewBase}>{r.baseG.toFixed(1)}</span>
                  <span className={styles.previewNew}>{r.newG.toFixed(1)}</span>
                  <span className={styles.previewPct}>{r.pct.toFixed(3)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className={styles.footer}>
          <button className={styles.btnCancel} onClick={onClose}>Cancel</button>
          <button
            className={styles.btnApply}
            disabled={!canPreview}
            onClick={applyScale}
          >
            Apply Scaled Recipe
          </button>
        </div>

      </div>
    </div>
  );
}
